import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Stack, 
  Alert, 
  Button, 
  Box, 
  Typography, 
  Container, 
  Skeleton, 
  Fade, 
  Paper,
  BottomNavigation, 
  BottomNavigationAction
} from '@mui/material';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { Event, Announcement } from '../types/Event';
import EventButton from './EventButton';
import JoinModal from './JoinModal';
import ManageEventModal from './ManageEventModal';
import AddEventModal from './AddEventModal';
import DuplicateEventModal from './DuplicateEventModal';
import AnnouncementModal from './AnnouncementModal';
import TournamentModal from './TournamentModalView';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../config';
import { canManageEvent } from '../utils/permissions';
import { 
  Settings as SettingsIcon, 
  Add as AddIcon, 
  Event as EventIcon,
  SportsTennis,
  SportsBasketball,
  SportsSoccer,
  SportsVolleyball,
  SportsBaseball,
  SportsRugby,
  SportsGolf,
  DirectionsRun,
  FitnessCenter,
  Pool,
  Campaign as CampaignIcon
} from '@mui/icons-material';

const ICON_MAP: { [key: string]: React.ElementType } = {
  SportsTennis,
  SportsBasketball,
  SportsSoccer,
  SportsVolleyball,
  SportsBaseball,
  SportsRugby,
  SportsGolf,
  DirectionsRun,
  FitnessCenter,
  Pool,
};

const JAPANESE_FONT_STACK = '"Hiragino Kaku Gothic ProN", "Hiragino Sans", Meiryo, sans-serif';

const EventList: React.FC = () => {
  const [events, setEvents] = useState<Event[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAddEventModalOpen, setIsAddEventModalOpen] = useState(false);
  const [isJoinModalOpen, setIsJoinModalOpen] = useState(false);
  const [isManageModalOpen, setIsManageModalOpen] = useState(false);
  const [isTournamentModalOpen, setIsTournamentModalOpen] = useState(false);
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
  const [siteName, setSiteName] = useState<string>('');
  const [siteIcon, setSiteIcon] = useState<string>('SportsTennis');
  const [siteHeroBackground, setSiteHeroBackground] = useState<string>('linear-gradient(135deg, #1a237e 0%, #0d47a1 100%)');
  const [siteNameLoading, setSiteNameLoading] = useState(true);
  const [siteNameError, setSiteNameError] = useState<string | null>(null);
  const [isOwner, setIsOwner] = useState(false);
  const [sitePermissions, setSitePermissions] = useState<any>(null);
  const [siteAnnouncements, setSiteAnnouncements] = useState<Announcement[]>([]);
  const [bottomNav, setBottomNav] = useState<string>('');
  const [pageSize] = useState(20);
  const [visibleCount, setVisibleCount] = useState(20);
  const [isLightBackground, setIsLightBackground] = useState(false);
  const [isAnnouncementModalOpen, setIsAnnouncementModalOpen] = useState(false);
  
  const latestAnnouncementData = useMemo((): { announcement: Announcement, event?: Event, isSiteAnnouncement?: boolean } | null => {
    let latest: { announcement: Announcement, event?: Event, isSiteAnnouncement?: boolean } | null = null;
    
    // Check event announcements
    events.forEach(event => {
      if (event.announcements && event.announcements.length > 0) {
        event.announcements.forEach(ann => {
          if (!latest || new Date(ann.createdAt) > new Date(latest!.announcement.createdAt)) {
            latest = { announcement: ann, event };
          }
        });
      }
    });

    // Check site announcements
    if (siteAnnouncements && siteAnnouncements.length > 0) {
      siteAnnouncements.forEach(ann => {
        if (!latest || new Date(ann.createdAt) > new Date(latest!.announcement.createdAt)) {
          latest = { announcement: ann, isSiteAnnouncement: true };
        }
      });
    }

    return latest;
  }, [events, siteAnnouncements]);

  const { isAuthenticated, isLoading, authChecked, checkAuth, user } = useAuth();
  const { route, eventId } = useParams<{ route: string; eventId?: string }>();
  const navigate = useNavigate();

  // Helper to determine if a background color is light
  const checkIsLightBackground = (background: string) => {
    // Default to false (dark background / white text)
    let isLight = false;

    // Handle solid hex colors
    if (background.startsWith('#')) {
      const hex = background.replace('#', '');
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      // Calculate brightness
      const brightness = (r * 299 + g * 587 + b * 114) / 1000;
      isLight = brightness > 128;
    } 
    // Handle linear-gradients (approximate by checking the first color if it's hex)
    else if (background.includes('linear-gradient')) {
      const match = background.match(/#[a-fA-F0-9]{6}/);
      if (match) {
        const hex = match[0].replace('#', '');
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        const brightness = (r * 299 + g * 587 + b * 114) / 1000;
        isLight = brightness > 150; // Slightly higher threshold for gradients
      }
    }

    setIsLightBackground(isLight);
  };

  const fetchSiteName = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/public/site/${route}`);
      setSiteName(response.data.siteName);
      if (response.data.icon) {
        setSiteIcon(response.data.icon);
      }
      if (response.data.heroBackground) {
        setSiteHeroBackground(response.data.heroBackground);
        checkIsLightBackground(response.data.heroBackground);
      }
      if (response.data.announcements) {
        setSiteAnnouncements(response.data.announcements);
      }
      
      if (isAuthenticated) {
        const authResponse = await axios.get(`${API_BASE_URL}/sites/${route}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        });
        setIsOwner(!!authResponse.data.isOwner);
        setSitePermissions(authResponse.data.permissions || null);
      }
      
      setSiteNameLoading(false);
    } catch (err) {
      console.error('Error loading site name:', err);
      if (axios.isAxiosError(err) && err.response?.status === 404) {
        setSiteNameError(`找不到群組 "${route}"。`);
      } else {
        setSiteNameError('無法載入群組名稱。');
      }
      setSiteNameLoading(false);
    }
  }, [isAuthenticated, route]);

  const fetchEvents = useCallback(async () => {
    try {
      const headers = {
        Authorization: `Bearer ${localStorage.getItem('token')}`
      };
      if (isAuthenticated && localStorage.getItem('token') !== undefined) {
        const response = await axios.get(
          `${API_BASE_URL}/events/all/${route}`, 
          { headers }
        );
        setEvents(response.data);
      } else {
        const response = await axios.get(`${API_BASE_URL}/public/site/${route}/events`);
        setEvents(response.data);
      }
      setLoading(false);
    } catch (err) {
      console.error('載入活動時發生錯誤:', err);
      setError('無法載入活動。請稍後再試。');
      setLoading(false);
    }
  }, [isAuthenticated, route]);

  // Handle direct event access via URL
  useEffect(() => {
    const fetchDirectEvent = async () => {
      if (!eventId) return;
      
      try {
        // First check if event is already in the events list
        const eventInList = events.find(event => event._id === eventId);
        if (eventInList) {
          setSelectedEvent(eventInList);
          setIsJoinModalOpen(true);
          return;
        }
        
        // If not in list (e.g., private event), fetch it directly
        let response;
        if (isAuthenticated && localStorage.getItem('token')) {
          response = await axios.get(`${API_BASE_URL}/events/${eventId}`, { 
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          });
        } else {
          response = await axios.get(`${API_BASE_URL}/public/events/${eventId}`);
        }
        setSelectedEvent(response.data);
        setIsJoinModalOpen(true);
      } catch (error: any) {
        console.error('Error fetching event:', error);
        navigate(`/${route}`);
      }
    };
    
    if (eventId && authChecked) {
      fetchDirectEvent();
    }
  }, [eventId, events, navigate, route, isAuthenticated, authChecked]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    if (!isAuthenticated) {
      setIsOwner(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (authChecked) {
      fetchEvents();
      fetchSiteName();
    }
  }, [authChecked, fetchEvents, fetchSiteName]);

  useEffect(() => {
    if (siteName) {
      document.title = `${siteName} | MatchPoint`;
    }
  }, [siteName]);

  const handleEventUpdated = (updatedEvent?: Event) => {
    fetchEvents();
    if (updatedEvent && selectedEvent && updatedEvent._id === selectedEvent._id) {
      setSelectedEvent(updatedEvent);
    }
  };

  const handleJoinClick = (event: Event) => {
    setSelectedEvent(event);
    setIsJoinModalOpen(true);
  };

  const handleManageClick = (event: Event) => {
    setSelectedEvent(event);
    setIsManageModalOpen(true);
  };

  const handleTournamentClick = (event: Event) => {
    setSelectedEvent(event);
    setIsTournamentModalOpen(true);
  };

  const handleDuplicateClick = (event: Event) => {
    setSelectedEvent(event);
    setIsDuplicateModalOpen(true);
  };

  const handleCloseModal = () => {
    setSelectedEvent(null);
    setIsJoinModalOpen(false);
    setIsManageModalOpen(false);
    setIsTournamentModalOpen(false);
    setIsDuplicateModalOpen(false);
    
    if (eventId) {
      navigate(`/${route}`);
    }
  };

  const handleCloseAddEventModal = () => {
    setIsAddEventModalOpen(false);
  };

  const handleEventAdded = () => {
    fetchEvents();
  };

  const handleEventDeleted = () => {
    setIsManageModalOpen(false);
    fetchEvents();
  };

  useEffect(() => {
    if (authChecked && isAuthenticated) {
      fetchEvents();
    }
  }, [fetchEvents, isAuthenticated, authChecked]);

  // Loading Skeleton
  if (!authChecked || isLoading || loading || siteNameLoading) {
    return (
      <Container maxWidth="md" sx={{ py: 4, fontFamily: JAPANESE_FONT_STACK }}>
        <Skeleton variant="rectangular" height={160} sx={{ borderRadius: 4, mb: 4 }} />
        <Stack spacing={3}>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rectangular" height={140} sx={{ borderRadius: 2 }} />
          ))}
        </Stack>
      </Container>
    );
  }

  if (siteNameError) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '60vh',
        gap: 2,
        fontFamily: JAPANESE_FONT_STACK
      }}>
        <Typography variant="h4" component="h1" gutterBottom color="text.secondary">
          群組未找到
        </Typography>
        <Button variant="contained" onClick={() => navigate('/')}>回首頁</Button>
      </Box>
    );
  }

  if (error) {
    return (
      <Container sx={{ mt: 4, fontFamily: JAPANESE_FONT_STACK }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  const displayedEvents = events.slice(0, visibleCount);
  const hasMore = events.length > visibleCount;

  const SiteIconComponent = ICON_MAP[siteIcon];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#FCFCFC', pb: 10, fontFamily: JAPANESE_FONT_STACK }}>
      {/* Hero Header */}
      <Box 
        sx={{ 
          background: siteHeroBackground,
          color: isLightBackground ? 'text.primary' : 'white',
          pt: { xs: 2, md: 8 },
          pb: { xs: 6, md: 10 },
          mb: -4,
          borderRadius: { xs: 0, md: '0 0 32px 32px' },
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          transition: 'all 0.3s ease-in-out'
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center' }}>
            <Box 
              sx={{ 
                display: 'inline-flex', 
                p: { xs: 1.5, md: 2 }, 
                bgcolor: isLightBackground ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.1)', 
                borderRadius: '50%',
                mb: { xs: 1.5, md: 2 },
                backdropFilter: 'blur(10px)',
                justifyContent: 'center',
                alignItems: 'center',
                minWidth: { xs: 64, md: 80 },
                minHeight: { xs: 64, md: 80 },
                boxShadow: isLightBackground ? 'none' : '0 4px 12px rgba(0,0,0,0.1)'
              }}
            >
              {SiteIconComponent ? (
                <SiteIconComponent sx={{ fontSize: { xs: 32, md: 40 } }} />
              ) : (
                <Typography sx={{ fontSize: { xs: 32, md: 40 }, lineHeight: 1 }}>
                  {siteIcon}
                </Typography>
              )}
            </Box>
            <Typography 
              variant="h3" 
              component="h1" 
              fontWeight="700" 
              sx={{ 
                mb: 1,
                fontSize: { xs: '1.75rem', md: '2.5rem' },
                letterSpacing: '0.05em',
                textShadow: isLightBackground ? 'none' : '0 2px 4px rgba(0,0,0,0.2)'
              }}
            >
              {siteName}
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Main Content */}
      <Container maxWidth="md">
        <Stack spacing={3}>
          {/* Controls Bar */}
          <Paper 
            elevation={0}
            sx={{ 
              p: 2, 
              borderRadius: 4, 
              display: 'flex', 
              justifyContent: 'space-between',
              alignItems: 'center',
              bgcolor: 'white',
              border: '1px solid',
              borderColor: 'grey.100',
              boxShadow: '0 2px 12px rgba(0,0,0,0.03)'
            }}
          >
            {latestAnnouncementData ? (
              <Box 
                onClick={() => {
                  if (latestAnnouncementData.event) {
                    handleJoinClick(latestAnnouncementData.event);
                  } else if (latestAnnouncementData.isSiteAnnouncement) {
                    setIsAnnouncementModalOpen(true);
                  }
                }}
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1.5,
                  cursor: latestAnnouncementData.event ? 'pointer' : 'default',
                  flex: 1,
                  minWidth: 0,
                  mr: 2,
                  '&:hover': latestAnnouncementData.event ? { opacity: 0.8 } : {}
                }}
              >
                <Box sx={{ 
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <CampaignIcon color="error" sx={{ fontSize: 24 }} />
                  {latestAnnouncementData.announcement.isImportant && (
                    <Box sx={{
                      position: 'absolute',
                      top: -2,
                      right: -2,
                      width: 8,
                      height: 8,
                      bgcolor: 'error.main',
                      borderRadius: '50%',
                      border: '1px solid white'
                    }} />
                  )}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle2" fontWeight="700" noWrap sx={{ color: 'text.primary' }}>
                    {latestAnnouncementData.announcement.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap display="block">
                    {new Date(latestAnnouncementData.announcement.createdAt).toLocaleDateString()} 
                    {latestAnnouncementData.isSiteAnnouncement ? ' • 群組公告' : ' • 點擊查看'}
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EventIcon color="action" sx={{ opacity: 0.7, fontSize: 20 }} />
                <Typography variant="subtitle1" fontWeight="600" sx={{ color: 'text.primary', letterSpacing: '0.05em' }}>
                  近期活動
                </Typography>
              </Box>
            )}
          </Paper>

          {/* Events List */}
          {displayedEvents.length === 0 ? (
            <Box 
              sx={{ 
                textAlign: 'center', 
                py: 10,
                bgcolor: 'white',
                borderRadius: 4,
                border: '1px dashed',
                borderColor: 'grey.200',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}
            >
              <EventIcon sx={{ fontSize: 48, color: 'grey.300', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" sx={{ mb: 1, fontWeight: 500 }}>
                目前沒有活動
              </Typography>
              <Typography variant="body2" color="text.disabled" sx={{ mb: 3 }}>
                稍後再回來查看，或聯絡管理員。
              </Typography>
              {isOwner && (
                <Button 
                  variant="contained" 
                  disableElevation
                  sx={{ 
                    mt: 1, 
                    borderRadius: 8, 
                    px: 4, 
                    bgcolor: 'primary.main',
                    '&:hover': { bgcolor: 'primary.dark' }
                  }} 
                  startIcon={<AddIcon />}
                  onClick={() => setIsAddEventModalOpen(true)}
                >
                  新增活動
                </Button>
              )}
            </Box>
          ) : (
            <Fade in={true}>
              <Stack spacing={2}>
                {displayedEvents.map((event) => (
                  <EventButton 
                    key={event._id}
                    event={event} 
                    onJoin={() => handleJoinClick(event)}
                    onManage={() => handleManageClick(event)}
                    onTournament={() => handleTournamentClick(event)}
                    onDuplicate={isOwner ? () => handleDuplicateClick(event) : undefined}
                    isAuthenticated={isAuthenticated}
                    isAdmin={isOwner}
                    siteSettings={typeof event.site === 'object' ? event.site.settings : {}}
                  />
                ))}
              </Stack>
            </Fade>
          )}

          {/* Load More Button */}
          {hasMore && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <Button 
                variant="text" 
                size="large"
                onClick={() => setVisibleCount((c) => c + pageSize)}
                sx={{ 
                  borderRadius: 8, 
                  px: 4,
                  py: 1,
                  color: 'text.secondary',
                  bgcolor: 'white',
                  border: '1px solid',
                  borderColor: 'grey.200',
                  '&:hover': { 
                    bgcolor: 'grey.50',
                    borderColor: 'grey.300',
                    color: 'text.primary'
                  },
                  transition: 'all 0.2s'
                }}
              >
                載入更多
              </Button>
            </Box>
          )}
        </Stack>
      </Container>

      {/* Modals */}
      {selectedEvent && isJoinModalOpen && (
        <JoinModal
          event={selectedEvent}
          onClose={handleCloseModal}
          onEventUpdated={handleEventUpdated}
        />
      )}
      {selectedEvent && isManageModalOpen && canManageEvent(selectedEvent, user?.userId, isOwner) && (
        <ManageEventModal
          event={selectedEvent}
          onClose={handleCloseModal}
          onEventUpdated={handleEventUpdated}
          onEventDeleted={handleEventDeleted}
          siteName={siteName}
          sitePermissions={sitePermissions}
        />
      )}
      {selectedEvent && isTournamentModalOpen && (
        <TournamentModal
          event={selectedEvent}
          onClose={handleCloseModal}
          onEventUpdated={handleEventUpdated}
          siteName={siteName}
          readOnly={!isOwner}
        />
      )}
      {isAddEventModalOpen && isOwner && (
        <AddEventModal
          onClose={handleCloseAddEventModal}
          onEventAdded={handleEventAdded}
          siteName={siteName}
          route={route || ''}
        />
      )}

      {selectedEvent && isDuplicateModalOpen && isOwner && (
        <DuplicateEventModal
          event={selectedEvent}
          open={isDuplicateModalOpen}
          onClose={handleCloseModal}
          onDuplicated={fetchEvents}
        />
      )}

      {/* Site Announcement Modal */}
      {latestAnnouncementData?.isSiteAnnouncement && (
        <AnnouncementModal
          open={isAnnouncementModalOpen}
          onClose={() => setIsAnnouncementModalOpen(false)}
          announcements={[latestAnnouncementData.announcement]}
          title="群組公告"
          isParticipant={true} // Assuming public site announcements are visible to everyone
        />
      )}

      {/* Admin Bottom Nav */}
      {isOwner && (
        <Paper 
          sx={{ 
            position: 'fixed', 
            bottom: 0, 
            left: 0, 
            right: 0, 
            zIndex: 1000,
            borderRadius: '20px 20px 0 0',
            overflow: 'hidden',
            boxShadow: '0 -4px 20px rgba(0,0,0,0.06)'
          }} 
          elevation={0}
        >
          <BottomNavigation
            value={bottomNav}
            onChange={(_, v) => {
              setBottomNav(v);
              switch (v) {
                case 'add':
                  setIsAddEventModalOpen(true);
                  break;
                case 'settings':
                  navigate(`/${route}/site-management`);
                  break;
                default:
                  break;
              }
            }}
            showLabels
            sx={{ 
              height: 64,
              borderTop: '1px solid',
              borderColor: 'grey.100'
            }}
          >
            <BottomNavigationAction 
              value="add" 
              label="新增活動" 
              icon={<AddIcon sx={{ fontSize: 24 }} />} 
            />
            <BottomNavigationAction 
              value="settings" 
              label="群組設定" 
              icon={<SettingsIcon sx={{ fontSize: 24 }} />} 
            />
          </BottomNavigation>
        </Paper>
      )}
    </Box>
  );
};

export default EventList;
